'use client'

import { useState, useCallback, useRef, useEffect } from 'react'
import { ExternalLink, MoveUpRight, Play, X } from 'lucide-react'
import { DisplayModal } from '@/components/ui/display-modal'
import { cn } from '@/utils/styles/cn'
import { createPortal } from 'react-dom'
import { motion, useMotionValue, useTransform } from 'framer-motion'
import { useTutorialMiniPlayerZustand } from '@/features/tutorial-center/use-tutorial-mini-player-zustand'
import { YOUTUBE_VIDEO_IDS, getYouTubeWatchUrl } from '@/features/agents/config/agent-video-config'

export interface ITutorialItem {
    title: string
    url: string
    posterUrl?: string
}

export interface ITutorialSection {
    title: string
    subtitle?: string
    tutorials: ITutorialItem[]
}

export const TUTORIAL_MINI_PLAYER_ID = 'superscale-tutorial-mini-player'

const PLACEHOLDER_SECTIONS: ITutorialSection[] = [
    {
        title: 'Get Started',
        subtitle: "Watch these first, in order, when you're new. This is your onboarding path.",
        tutorials: [
            {
                title: 'How to Lower CPAs with an Agentic Ad Workflow (Superscale Overview)',
                url: getYouTubeWatchUrl(YOUTUBE_VIDEO_IDS.superscaleOverview),
            },
            {
                title: 'Research, Create, Analyze: Full AI Ad Workflow in Superscale (Live Demo)',
                url: getYouTubeWatchUrl(YOUTUBE_VIDEO_IDS.superscaleUsecases),
            },
            {
                title: 'Set Up Your AI Ad Agent in 5 Minutes (Superscale Getting Started Guide)',
                url: getYouTubeWatchUrl(YOUTUBE_VIDEO_IDS.gettingStarted),
            },
            {
                title: 'How To Ensure Logo And Brand Consistency in 2 Minutes (Fix all logo issues)',
                url: getYouTubeWatchUrl(YOUTUBE_VIDEO_IDS.brandLogoConsistency),
            },
            {
                title: 'How to Get Better AI Ads: Context Files Explained (Superscale Deep Dive)',
                url: getYouTubeWatchUrl(YOUTUBE_VIDEO_IDS.contextFiles),
            },
        ],
    },
    {
        title: 'Create Better Ads',
        subtitle: "Watch these when you're actively producing. Order doesn't matter as much — pick what's relevant.",
        tutorials: [
            {
                title: 'How to Edit AI-Generated Static Ads with Superscale (Small Details to Bulk Edits in Chat)',
                url: getYouTubeWatchUrl(YOUTUBE_VIDEO_IDS.editStaticAds),
            },
        ],
    },
]

interface ISuperscaleTutorialCenterModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    sections?: ITutorialSection[]
}

function getYouTubeVideoId(url: string): string | null {
    try {
        const parsed = new URL(url)
        if (parsed.hostname.includes('youtu.be')) {
            return parsed.pathname.replace('/', '') || null
        }
        if (parsed.hostname.includes('youtube.com')) {
            const fromQuery = parsed.searchParams.get('v')
            if (fromQuery) return fromQuery
            const pathParts = parsed.pathname.split('/').filter(Boolean)
            const embedIndex = pathParts.findIndex((part) => part === 'embed')
            if (embedIndex >= 0) return pathParts[embedIndex + 1] ?? null
        }
        return null
    } catch {
        return null
    }
}

function resolveVideoUrl(item: Pick<ITutorialItem, 'url'>): string {
    return item.url
}

function isYouTubeUrl(url: string): boolean {
    try {
        const hostname = new URL(url).hostname
        return hostname.includes('youtube.com') || hostname.includes('youtu.be')
    } catch {
        return false
    }
}

function getYouTubeEmbedUrl(url: string, autoplay = false): string | null {
    const id = getYouTubeVideoId(url)
    if (!id) return null
    const params = new URLSearchParams({
        rel: '0',
        modestbranding: '1',
        playsinline: '1',
        enablejsapi: '1',
    })
    if (autoplay) params.set('autoplay', '1')
    return `https://www.youtube.com/embed/${id}?${params.toString()}`
}

function getThumbnailUrl(url: string, quality: 'maxresdefault' | 'hqdefault' = 'hqdefault'): string | null {
    const id = getYouTubeVideoId(url)
    return id ? `https://img.youtube.com/vi/${id}/${quality}.jpg` : null
}

function TutorialCard({ tutorial, onClick }: { tutorial: ITutorialItem; onClick: (tutorial: ITutorialItem) => void }) {
    const videoUrl = resolveVideoUrl(tutorial)
    const primaryThumbnail =
        tutorial.posterUrl ?? (isYouTubeUrl(videoUrl) ? getThumbnailUrl(videoUrl, 'maxresdefault') : null)
    const fallbackThumbnail =
        tutorial.posterUrl ?? (isYouTubeUrl(videoUrl) ? getThumbnailUrl(videoUrl, 'hqdefault') : null)
    const [thumbnail, setThumbnail] = useState<string | null>(primaryThumbnail)

    useEffect(() => {
        setThumbnail(primaryThumbnail)
    }, [primaryThumbnail])

    return (
        <button
            type="button"
            onClick={() => onClick(tutorial)}
            className="group relative flex w-full flex-col overflow-hidden rounded-[14px] border border-[#E7E5E4] bg-white/95 text-left shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition-all duration-200 hover:-translate-y-px hover:border-[#DDD6D3] hover:shadow-[0_12px_28px_rgba(0,0,0,0.12)]"
        >
            <div className="relative aspect-video w-full overflow-hidden bg-stone-100">
                {thumbnail ? (
                    <img
                        src={thumbnail}
                        alt={tutorial.title}
                        onError={() => {
                            if (thumbnail !== fallbackThumbnail) {
                                setThumbnail(fallbackThumbnail)
                            }
                        }}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                    />
                ) : (
                    <div className="h-full w-full bg-linear-to-br from-stone-100 via-stone-200 to-stone-100" />
                )}
                <div className="absolute inset-0 bg-black/15 transition-colors group-hover:bg-black/5" />
                <div className="absolute inset-0 flex items-center justify-center">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/40 bg-white/90 shadow-[0_4px_12px_rgba(0,0,0,0.2)] transition-shadow group-hover:shadow-[0_8px_18px_rgba(0,0,0,0.24)]">
                        <Play className="h-4 w-4 text-stone-900" />
                    </span>
                </div>
            </div>
            <div className="flex items-center justify-between gap-3 px-4 py-3.5">
                <p className="line-clamp-2 text-sm font-medium leading-[1.35] text-stone-800">{tutorial.title}</p>
                <MoveUpRight className="h-4 w-4 shrink-0 text-stone-500 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </div>
        </button>
    )
}

function TutorialSection({
    section,
    onTutorialClick,
    showTitle = true,
}: {
    section: ITutorialSection
    onTutorialClick: (tutorial: ITutorialItem) => void
    showTitle?: boolean
}) {
    return (
        <section className="space-y-3">
            {showTitle ? (
                <div className="md:ml-[7.5%]">
                    <h3 className="text-[13px] font-semibold tracking-[-0.01em] text-stone-800">{section.title}</h3>
                    {section.subtitle ? <p className="mt-0.5 text-[12px] text-[#6B7280]">{section.subtitle}</p> : null}
                </div>
            ) : null}
            <div className="grid grid-cols-1 gap-4 md:mx-auto md:max-w-[85%] md:grid-cols-3">
                {section.tutorials.map((tutorial) => (
                    <TutorialCard
                        key={`${section.title}-${tutorial.title}`}
                        tutorial={tutorial}
                        onClick={onTutorialClick}
                    />
                ))}
            </div>
        </section>
    )
}

function TutorialMiniPlayer({ tutorial, onClose }: { tutorial: ITutorialItem; onClose: () => void }) {
    const HEADER_HEIGHT = 44
    const MIN_VIDEO_WIDTH = 320
    const MIN_VIDEO_HEIGHT = 180
    const MIN_TOTAL_HEIGHT = MIN_VIDEO_HEIGHT + HEADER_HEIGHT
    const ASPECT_RATIO = 16 / 9

    const [dragging, setDragging] = useState(false)
    const [resizing, setResizing] = useState(false)
    const [mounted, setMounted] = useState(false)

    const dragStateRef = useRef<{ offsetX: number; offsetY: number } | null>(null)
    const resizeStateRef = useRef<{
        handle: 'nw' | 'ne' | 'sw' | 'se' | 'n' | 'e' | 's' | 'w'
        startX: number
        startY: number
        startWidth: number
        startHeight: number
        startLeft: number
        startTop: number
    } | null>(null)
    const playerRef = useRef<HTMLDivElement | null>(null)
    const rafRef = useRef<number | null>(null)
    const debounceRef = useRef<number | null>(null)
    const pendingRef = useRef<{ x?: number; y?: number; width?: number; height?: number }>({})
    const iframeRef = useRef<HTMLIFrameElement | null>(null)
    const videoRef = useRef<HTMLVideoElement | null>(null)

    const xMotion = useMotionValue(16)
    const yMotion = useMotionValue(62)
    const widthMotion = useMotionValue(560)
    const heightMotion = useMotionValue(Math.round(560 / ASPECT_RATIO) + HEADER_HEIGHT)
    const videoHeightMotion = useTransform(heightMotion, (height) => Math.max(MIN_VIDEO_HEIGHT, height - HEADER_HEIGHT))

    const videoUrl = resolveVideoUrl(tutorial)
    const isYouTube = isYouTubeUrl(videoUrl)
    const embedUrl = isYouTube ? getYouTubeEmbedUrl(videoUrl, true) : null

    const pauseEmbeddedVideo = useCallback(() => {
        if (isYouTube) {
            iframeRef.current?.contentWindow?.postMessage(
                JSON.stringify({
                    event: 'command',
                    func: 'pauseVideo',
                    args: [],
                }),
                '*'
            )
            return
        }
        videoRef.current?.pause()
    }, [isYouTube])

    useEffect(() => {
        setMounted(true)
        return () => {
            setMounted(false)
            if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
            if (debounceRef.current != null) window.clearTimeout(debounceRef.current)
        }
    }, [])

    const clampLayout = useCallback(
        (next: { x: number; y: number; width: number; height: number }) => {
            const maxWidth = window.innerWidth - 16
            const maxHeight = window.innerHeight - 16
            const width = Math.max(MIN_VIDEO_WIDTH, Math.min(maxWidth, next.width))
            const height = Math.max(MIN_TOTAL_HEIGHT, Math.min(maxHeight, next.height))
            const x = Math.max(8, Math.min(window.innerWidth - width - 8, next.x))
            const y = Math.max(8, Math.min(window.innerHeight - height - 8, next.y))
            return { x, y, width, height }
        },
        [MIN_TOTAL_HEIGHT]
    )

    const flushPending = useCallback(() => {
        rafRef.current = null
        const next = {
            x: pendingRef.current.x ?? xMotion.get(),
            y: pendingRef.current.y ?? yMotion.get(),
            width: pendingRef.current.width ?? widthMotion.get(),
            height: pendingRef.current.height ?? heightMotion.get(),
        }
        const clamped = clampLayout(next)
        xMotion.set(clamped.x)
        yMotion.set(clamped.y)
        widthMotion.set(clamped.width)
        heightMotion.set(clamped.height)
        pendingRef.current = {}
    }, [clampLayout, heightMotion, widthMotion, xMotion, yMotion])

    const scheduleLayoutUpdate = useCallback(
        (next: { x?: number; y?: number; width?: number; height?: number }) => {
            pendingRef.current = { ...pendingRef.current, ...next }
            if (rafRef.current == null) {
                rafRef.current = requestAnimationFrame(flushPending)
            }
            if (debounceRef.current != null) window.clearTimeout(debounceRef.current)
            debounceRef.current = window.setTimeout(flushPending, 32)
        },
        [flushPending]
    )

    const stopDragging = useCallback(() => {
        dragStateRef.current = null
        setDragging(false)
        flushPending()
    }, [flushPending])

    const stopAllInteractions = useCallback(() => {
        dragStateRef.current = null
        resizeStateRef.current = null
        setDragging(false)
        setResizing(false)
        flushPending()
    }, [flushPending])

    const handleMouseMove = useCallback(
        (event: MouseEvent) => {
            if (!dragStateRef.current) return
            const playerWidth = playerRef.current?.offsetWidth ?? widthMotion.get()
            const playerHeight = playerRef.current?.offsetHeight ?? heightMotion.get()
            const nextX = event.clientX - dragStateRef.current.offsetX
            const nextY = event.clientY - dragStateRef.current.offsetY
            const maxX = window.innerWidth - playerWidth - 8
            const maxY = window.innerHeight - playerHeight - 8

            scheduleLayoutUpdate({
                x: Math.max(8, Math.min(maxX, nextX)),
                y: Math.max(8, Math.min(maxY, nextY)),
            })
        },
        [heightMotion, scheduleLayoutUpdate, widthMotion]
    )

    const stopResizing = useCallback(() => {
        resizeStateRef.current = null
        setResizing(false)
        flushPending()
    }, [flushPending])

    const handleResizeMove = useCallback(
        (event: MouseEvent) => {
            if (!resizeStateRef.current) return
            const { handle, startX, startY, startWidth, startHeight, startLeft, startTop } = resizeStateRef.current
            const dx = event.clientX - startX
            const dy = event.clientY - startY

            let nextWidth = startWidth
            let nextHeight = startHeight
            let nextLeft = startLeft
            let nextTop = startTop

            if (handle === 'e') nextWidth = startWidth + dx
            if (handle === 'w') {
                nextWidth = startWidth - dx
                nextLeft = startLeft + dx
            }
            if (handle === 's') nextHeight = startHeight + dy
            if (handle === 'n') {
                nextHeight = startHeight - dy
                nextTop = startTop + dy
            }

            if (handle === 'ne' || handle === 'se' || handle === 'nw' || handle === 'sw') {
                if (handle === 'ne' || handle === 'se') {
                    nextWidth = startWidth + dx
                } else {
                    nextWidth = startWidth - dx
                }
                nextWidth = Math.max(MIN_VIDEO_WIDTH, Math.min(window.innerWidth - 16, nextWidth))
                nextHeight = nextWidth / ASPECT_RATIO + HEADER_HEIGHT

                if (handle === 'nw' || handle === 'sw') nextLeft = startLeft + (startWidth - nextWidth)
                if (handle === 'nw' || handle === 'ne') nextTop = startTop + (startHeight - nextHeight)
            }

            const maxWidth = window.innerWidth - 16
            const maxHeight = window.innerHeight - 16
            nextWidth = Math.max(MIN_VIDEO_WIDTH, Math.min(maxWidth, nextWidth))
            nextHeight = Math.max(MIN_TOTAL_HEIGHT, Math.min(maxHeight, nextHeight))
            const boundedLeft = Math.max(8, Math.min(window.innerWidth - nextWidth - 8, nextLeft))
            const boundedTop = Math.max(8, Math.min(window.innerHeight - nextHeight - 8, nextTop))

            scheduleLayoutUpdate({ width: nextWidth, height: nextHeight, x: boundedLeft, y: boundedTop })
        },
        [ASPECT_RATIO, MIN_TOTAL_HEIGHT, scheduleLayoutUpdate]
    )

    const handleMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement
        if (target.closest('[data-no-drag="true"]')) return

        const card = event.currentTarget.getBoundingClientRect()
        dragStateRef.current = { offsetX: event.clientX - card.left, offsetY: event.clientY - card.top }
        setDragging(true)
        event.preventDefault()
    }, [])

    useEffect(() => {
        if (!dragging) return
        window.addEventListener('mousemove', handleMouseMove)
        window.addEventListener('mouseup', stopDragging)
        window.addEventListener('mouseleave', stopDragging)
        window.addEventListener('blur', stopDragging)
        window.addEventListener('pointerup', stopDragging)
        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
            window.removeEventListener('mouseup', stopDragging)
            window.removeEventListener('mouseleave', stopDragging)
            window.removeEventListener('blur', stopDragging)
            window.removeEventListener('pointerup', stopDragging)
        }
    }, [dragging, handleMouseMove, stopDragging])

    useEffect(() => {
        if (!resizing) return
        window.addEventListener('mousemove', handleResizeMove)
        window.addEventListener('mouseup', stopResizing)
        window.addEventListener('mouseleave', stopResizing)
        window.addEventListener('blur', stopResizing)
        window.addEventListener('pointerup', stopResizing)
        return () => {
            window.removeEventListener('mousemove', handleResizeMove)
            window.removeEventListener('mouseup', stopResizing)
            window.removeEventListener('mouseleave', stopResizing)
            window.removeEventListener('blur', stopResizing)
            window.removeEventListener('pointerup', stopResizing)
        }
    }, [handleResizeMove, resizing, stopResizing])

    useEffect(() => {
        // Safety net: if interaction flags ever desync, terminate both.
        const onWindowBlur = () => stopAllInteractions()
        window.addEventListener('blur', onWindowBlur)
        return () => window.removeEventListener('blur', onWindowBlur)
    }, [stopAllInteractions])

    const startResize = useCallback(
        (handle: 'nw' | 'ne' | 'sw' | 'se' | 'n' | 'e' | 's' | 'w') => (event: React.MouseEvent<HTMLDivElement>) => {
            event.preventDefault()
            event.stopPropagation()
            resizeStateRef.current = {
                handle,
                startX: event.clientX,
                startY: event.clientY,
                startWidth: widthMotion.get(),
                startHeight: heightMotion.get(),
                startLeft: xMotion.get(),
                startTop: yMotion.get(),
            }
            setResizing(true)
        },
        [heightMotion, widthMotion, xMotion, yMotion]
    )

    if (!videoUrl || !mounted) return null

    return createPortal(
        <motion.div
            ref={playerRef}
            id={TUTORIAL_MINI_PLAYER_ID}
            data-dialog-allow-interaction="true"
            className={cn(
                'fixed z-9999 pointer-events-auto w-[420px] max-w-[calc(100vw-16px)] overflow-hidden rounded-[18px] border border-white/20 bg-black shadow-[0_2px_8px_rgba(0,0,0,0.2),0_18px_32px_rgba(0,0,0,0.28)]',
                dragging ? 'cursor-grabbing select-none' : 'cursor-grab'
            )}
            style={{ left: xMotion, top: yMotion, width: widthMotion, height: heightMotion }}
            onMouseDown={handleMouseDown}
        >
            <div className="flex h-11 items-center justify-between gap-2 border-b border-white/10 bg-linear-to-r from-black/75 via-black/60 to-black/75 px-3 py-2 text-white">
                <div className="min-w-0">
                    <p className="truncate text-xs font-semibold">{tutorial.title}</p>
                </div>
                <div className="flex items-center gap-1.5">
                    <button
                        data-no-drag="true"
                        type="button"
                        onClick={(event) => {
                            event.stopPropagation()
                            pauseEmbeddedVideo()
                            window.open(videoUrl, '_blank', 'noopener,noreferrer')
                        }}
                        className="rounded-md p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                        aria-label="Open video"
                    >
                        <ExternalLink className="h-3.5 w-3.5" />
                    </button>
                    <button
                        data-no-drag="true"
                        type="button"
                        onPointerDown={(event) => {
                            // Consume pointer down so underlying dialog outside handlers never see this interaction.
                            event.preventDefault()
                            event.stopPropagation()
                            onClose()
                        }}
                        onClick={(event) => {
                            event.stopPropagation()
                        }}
                        className="rounded-md p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                        aria-label="Close player"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>
            <motion.div
                className="w-full overflow-hidden rounded-b-[18px]"
                style={{ height: videoHeightMotion, pointerEvents: dragging || resizing ? 'none' : 'auto' }}
            >
                {isYouTube && embedUrl ? (
                    <iframe
                        ref={iframeRef}
                        data-no-drag="true"
                        title={tutorial.title}
                        src={embedUrl}
                        className="h-full w-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                    />
                ) : (
                    <video
                        ref={videoRef}
                        data-no-drag="true"
                        className="h-full w-full"
                        src={videoUrl}
                        poster={tutorial.posterUrl}
                        autoPlay
                        controls
                        playsInline
                    />
                )}
            </motion.div>
            <div
                data-no-drag="true"
                onMouseDown={startResize('nw')}
                className="absolute left-0 top-0 h-4 w-4 cursor-nwse-resize"
            />
            <div
                data-no-drag="true"
                onMouseDown={startResize('ne')}
                className="absolute right-0 top-0 h-4 w-4 cursor-nesw-resize"
            />
            <div
                data-no-drag="true"
                onMouseDown={startResize('sw')}
                className="absolute bottom-0 left-0 h-4 w-4 cursor-nesw-resize"
            />
            <div
                data-no-drag="true"
                onMouseDown={startResize('se')}
                className="absolute bottom-0 right-0 h-4 w-4 cursor-nwse-resize"
            />
            <div
                data-no-drag="true"
                onMouseDown={startResize('n')}
                className="absolute left-4 right-4 top-0 h-2 cursor-ns-resize"
            />
            <div
                data-no-drag="true"
                onMouseDown={startResize('s')}
                className="absolute bottom-0 left-4 right-4 h-2 cursor-ns-resize"
            />
            <div
                data-no-drag="true"
                onMouseDown={startResize('w')}
                className="absolute bottom-4 left-0 top-4 w-2 cursor-ew-resize"
            />
            <div
                data-no-drag="true"
                onMouseDown={startResize('e')}
                className="absolute bottom-4 right-0 top-4 w-2 cursor-ew-resize"
            />
        </motion.div>,
        document.body
    )
}

export function SuperscaleTutorialCenterModal({
    open,
    onOpenChange,
    sections = PLACEHOLDER_SECTIONS,
}: ISuperscaleTutorialCenterModalProps) {
    const openTutorial = useTutorialMiniPlayerZustand((s) => s.openTutorial)

    return (
        <>
            <DisplayModal
                open={open}
                onOpenChange={(nextOpen) => {
                    onOpenChange(nextOpen)
                }}
                allowOutsidePointerEvents
                title="Superscale Tutorial Center"
                subtitle="Learn faster with quick, practical walkthroughs for every part of your workflow."
                bodyClassName="mt-2 space-y-6"
            >
                <div className="space-y-8">
                    {sections.map((section) => (
                        <TutorialSection
                            key={section.title}
                            section={section}
                            showTitle
                            onTutorialClick={(tutorial) => {
                                openTutorial(tutorial)
                            }}
                        />
                    ))}
                </div>
            </DisplayModal>
        </>
    )
}

export function SuperscaleTutorialMiniPlayerRenderer() {
    const activeTutorial = useTutorialMiniPlayerZustand((s) => s.activeTutorial)
    const closeTutorial = useTutorialMiniPlayerZustand((s) => s.closeTutorial)

    if (!activeTutorial) return null
    return <TutorialMiniPlayer tutorial={activeTutorial} onClose={closeTutorial} />
}
